<template>
  <div
    class="widget-scroll"
    :class="{
      'widget-scroll--at-top': scrollState.atTop,
      'widget-scroll--at-bottom': scrollState.atBottom,
      'widget-scroll--with-shadow': withShadow,
    }"
  >
    <div
      ref="scroll"
      class="widget-scroll__scroll"
      v-bind="{
        [scrollbarDataIdAttribute]: scrollBarId,
      }"
    >
      <div
        ref="content"
        class="widget-scroll__content"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import scrollbarService from '@widgets/shared/services/scrollbarService'
import { scrollbarDataIdAttribute } from '@widgets/shared/const'
import { useId, onMounted, reactive, onBeforeUnmount, useTemplateRef } from '@/composition/vue/compositionApi'
import { useResizeObserver } from '@/composition/resizeObserver'

const {
  maxHeight,
  withShadow = true,
  scrollBarId = useId(),
} = defineProps<{
  maxHeight?: string
  withShadow?: boolean
  scrollBarId?: string
}>()

const emit = defineEmits<{
  (e: 'resize-content-height', height: number): void
  (e: 'scroll', scrollTop: number): void
}>()

const scrollState = reactive({
  atTop: true,
  atBottom: true,
  contentHeight: 0,
})

let rafId: number | null = null
const scrollListener = () => {
  if (rafId) cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(() => {
    const { scrollTop, scrollBottom } = scrollbarService.getState(scrollBarId)!
    scrollState.atTop = scrollTop === 0
    scrollState.atBottom = scrollBottom === 0
    emit('scroll', scrollTop)
  })
}

const scrollRef = useTemplateRef<HTMLElement>('scroll')
useResizeObserver(scrollRef, () => {
  scrollListener()
})

const contentRef = useTemplateRef<HTMLElement>('content')
useResizeObserver(contentRef, () => {
  scrollListener()
  emit('resize-content-height', contentRef.value!.offsetHeight)
})

onMounted(() => {
  scrollbarService.addScrollListener(scrollBarId, scrollListener)
  scrollListener()
})

onBeforeUnmount(() => {
  scrollbarService.removeScrollListener(scrollBarId, scrollListener)
})
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

@mixin gradient($angle) {
  background: linear-gradient($angle, rgba(237, 237, 237, 0.57) 0%, rgba(244, 244, 244, 0) 100%);
}

.widget-scroll {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;

  &__scroll {
    overflow: auto;
    max-height: v-bind(maxHeight);
    // Subpixel compensation to prevent micro-scrollbars
    padding-bottom: 1px;
    margin-bottom: -1px;
    @include thumb-without-track;
  }

  &__content {
    position: relative;
    z-index: 0;
  }

  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
    pointer-events: none;
    left: 0;
    width: 100%;
    height: 16px;
    z-index: 1;
  }

  &--with-shadow {
    &::before {
      @include gradient(180deg);
      top: 0;
    }

    &::after {
      @include gradient(0deg);
      bottom: 0;
    }
  }

  &--at-top {
    &::before {
      display: none;
    }
  }

  &--at-bottom {
    &::after {
      display: none;
    }
  }
}
</style>
